/**
 * Хелперы для оптимистичных обновлений SWR
 * 
 * Optimistic Updates — UI обновляется мгновенно, до ответа сервера.
 * При ошибке — автоматический откат к предыдущему состоянию.
 */

import { MutatorOptions } from 'swr';

// Базовые опции для оптимистичных мутаций
export const optimisticOptions = {
  // Откатываем при ошибке (кроме AbortError)
  rollbackOnError: (error: Error) => error.name !== 'AbortError',
  // Заполняем кэш результатом мутации
  populateCache: true,
  // Ревалидируем после мутации для синхронизации с сервером
  revalidate: true,
} satisfies Partial<MutatorOptions>;

// Опции без ревалидации (для частых операций типа лайков)
export const optimisticNoRevalidate = {
  ...optimisticOptions,
  revalidate: false,
} satisfies Partial<MutatorOptions>;

/**
 * Создать опции для оптимистичного обновления
 * @param optimisticData Оптимистичные данные или функция (currentData) => newData
 * @param revalidate Ревалидировать после мутации (по умолчанию true)
 */
export function createOptimisticOptions<T>(
  optimisticData: T | ((current: T | undefined) => T),
  revalidate = true
): MutatorOptions<T> {
  return {
    optimisticData,
    rollbackOnError: (error: Error) => error.name !== 'AbortError',
    populateCache: true,
    revalidate,
  };
}

/**
 * Хелпер для оптимистичного добавления элемента в массив
 */
export function optimisticAdd<T>(item: T) {
  return (current: T[] | undefined): T[] => {
    return current ? [...current, item] : [item];
  };
}

/**
 * Хелпер для оптимистичного добавления элемента в начало массива
 */
export function optimisticPrepend<T>(item: T) {
  return (current: T[] | undefined): T[] => {
    return current ? [item, ...current] : [item];
  };
}

/**
 * Хелпер для оптимистичного удаления элемента из массива по id
 */
export function optimisticRemove<T extends { id: number | string }>(id: number | string) {
  return (current: T[] | undefined): T[] => {
    return current ? current.filter((item) => item.id !== id) : [];
  };
}

/**
 * Хелпер для оптимистичного обновления элемента в массиве
 */
export function optimisticUpdate<T extends { id: number | string }>(
  id: number | string,
  updates: Partial<T>
) {
  return (current: T[] | undefined): T[] => {
    if (!current) return [];
    return current.map((item) =>
      item.id === id ? { ...item, ...updates } : item
    );
  };
}

/**
 * Хелпер для оптимистичного инкремента счётчика
 */
export function optimisticIncrement<T>(field: keyof T, delta = 1) {
  return (current: T | undefined): T => {
    if (!current) return current as T;
    return {
      ...current,
      [field]: ((current[field] as number) || 0) + delta,
    };
  };
}

/**
 * Хелпер для оптимистичного toggle boolean поля
 */
export function optimisticToggle<T>(field: keyof T) {
  return (current: T | undefined): T => {
    if (!current) return current as T;
    return {
      ...current,
      [field]: !current[field],
    };
  };
}

/**
 * Интерфейс для пагинированных данных
 */
interface PaginatedData<T> {
  results: T[];
  count: number;
  page?: number;
  pages?: number;
}

/**
 * Хелпер для оптимистичного добавления в пагинированный список
 */
export function optimisticAddToPaginated<T>(item: T) {
  return (current: PaginatedData<T> | undefined): PaginatedData<T> => {
    if (!current) return { results: [item], count: 1 };
    return {
      ...current,
      results: [item, ...current.results],
      count: current.count + 1,
    };
  };
}

/**
 * Хелпер для оптимистичного обновления элемента в пагинированном списке
 */
export function optimisticUpdateInPaginated<T extends { id: number | string }>(
  id: number | string,
  updates: Partial<T>
) {
  return (current: PaginatedData<T> | undefined): PaginatedData<T> => {
    if (!current) return current as PaginatedData<T>;
    return {
      ...current,
      results: current.results.map((item) =>
        item.id === id ? { ...item, ...updates } : item
      ),
    };
  };
}

/**
 * Хелпер для оптимистичного удаления из пагинированного списка
 */
export function optimisticRemoveFromPaginated<T extends { id: number | string }>(
  id: number | string
) {
  return (current: PaginatedData<T> | undefined): PaginatedData<T> => {
    if (!current) return current as PaginatedData<T>;
    return {
      ...current,
      results: current.results.filter((item) => item.id !== id),
      count: Math.max(0, current.count - 1),
    };
  };
}
